import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Headphones } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { httpClient } from '../lib/http';
import { API_URL } from '../config/api';
import usePushBus from '../hooks/usePushBus';

/**
 * "Assistenza" card in Profilo. Polls the unread count once on mount
 * (and once when the page regains focus) and applies a pulsing red
 * outline + counter chip when there are unread admin messages or
 * bug-thread replies. Tapping the card navigates to the chat page.
 */
export default function SupportButton() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await httpClient.get(`${API_URL}/api/support/unread`);
      setUnread((data?.chat_unread || 0) + (data?.bugs_with_replies || 0));
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchUnread();
    // Slow safety-net poll. Real-time updates come via usePushBus below.
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [fetchUnread]);

  usePushBus(fetchUnread);

  const highlight = unread > 0;

  return (
    <button
      onClick={() => navigate('/app/assistenza')}
      className={`w-full glass-card-sm p-4 flex items-center justify-between transition-all ${
        highlight
          ? 'ring-2 ring-[#E8334A] ring-offset-0 animate-pulse-soft'
          : ''
      }`}
      data-testid="support-btn"
    >
      <span className={`flex items-center gap-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        <Headphones className={`w-5 h-5 ${highlight ? 'text-[#E8334A]' : (isDark ? 'text-white/40' : 'text-gray-400')}`} />
        <span className="text-left">
          <span className="block">{t('support.title')}</span>
          {highlight ? (
            <span className={`block text-xs ${isDark ? 'text-[#FFB1BC]' : 'text-[#E8334A]'}`}>
              {t('support.unreadHint', { count: unread })}
            </span>
          ) : (
            <span className={`block text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {t('support.subtitle')}
            </span>
          )}
        </span>
      </span>
      {highlight && (
        <span className="bg-[#E8334A] text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
          {unread}
        </span>
      )}
    </button>
  );
}
